import { CrossIcon } from "@eisox/icons";
import { Slot } from "@radix-ui/react-slot";

import { Circle } from "../Circle";
import { cn } from "../index";
import * as Sheet from "../Sheet";

/* -------------------------------------------------------------------------------------------------
 * Drawer
 * -----------------------------------------------------------------------------------------------*/

type DrawerProps = Sheet.SheetProps;

const Root = Sheet.Root;

Root.displayName = "Drawer";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = Sheet.TriggerProps;

const Trigger = Sheet.Trigger;

Trigger.displayName = "DrawerTrigger";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = Sheet.ContentProps;

const Content: React.FC<ContentProps> = ({ className, ...props }) => (
  <Sheet.Portal>
    <Sheet.Overlay className="z-1300" />
    <Sheet.Content {...props} className={cn("z-1300 flex w-full flex-col", className)} />
  </Sheet.Portal>
);

Content.displayName = "DrawerContent";

/* -------------------------------------------------------------------------------------------------
 * Header
 * -----------------------------------------------------------------------------------------------*/

interface HeaderProps {
  classNames?: {
    root?: string;
    title?: string;
    close?: string;
  };
  children?: string;
}

const Header: React.FC<HeaderProps> = ({ classNames, children }) => (
  <div className={cn("h-20 p-2.5 md:h-30 md:p-10 flex items-center justify-between bg-primary-blue", classNames?.root)}>
    <h2 className={cn("mb-0 font-semibold text-white md:text-2xl text-base", classNames?.title)}>{children}</h2>
    <Sheet.Close asChild>
      <Circle.Root backgroundColor="white" color="blue" style={{ cursor: "pointer" }}>
        <CrossIcon />
      </Circle.Root>
    </Sheet.Close>
  </div>
);

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

type GroupProps = React.ComponentPropsWithRef<"div">;

const Group: React.FC<GroupProps> = ({ className, ...props }) => (
  <div {...props} className={cn("flex flex-col gap-[5px]", className)} role="group" />
);

Group.displayName = "DrawerGroup";

/* -------------------------------------------------------------------------------------------------
 * Label
 * -----------------------------------------------------------------------------------------------*/

interface LabelProps extends React.ComponentPropsWithRef<"div"> {
  asChild?: boolean;
}

const Label: React.FC<LabelProps> = ({ asChild, className, ...props }) => {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      {...props}
      className={cn("font-semibold flex items-center gap-[5px] text-base text-primary-blue", className)}
    />
  );
};

Label.displayName = "DrawerLabel";

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/

type CloseProps = Sheet.CloseProps;

const Close = Sheet.Close;

Close.displayName = "DrawerClose";

export { Close, Content, Group, Header, Label, Root, Trigger };
export type { CloseProps, ContentProps, DrawerProps, GroupProps, HeaderProps, LabelProps, TriggerProps };
